/**
 * If a string is passed in it returns an array with that value being its only
 * record, else it returns the value passed in. However if undefined is passed
 * in this will return an empty array
 * @param {string|Array} val
 * @returns {Array}
 */
export function makeArray(val) {
  if (val === undefined) return []
  if (val instanceof Array === false) return [val]
  return val
}

/**
 * Get the matches between two arrays
 * @param {Array<string>} a
 * @param {Array<string>} b
 * @returns {Array<string>}
 */
export function getMatches(a, b) {
  var result = [],
    index = 0,
    hashMap = {}
  a.forEach(aVal => {
    while (index < b.length) {
      var bVal = b[index]
      hashMap[bVal] = true
      index++
      if (hashMap[aVal]) break
    }
    if (hashMap[aVal]) result.push(aVal)
  })
  return result
}

/**
 * Get all the duplicate values in an array
 * @param {Array} arr
 * @returns {Array}
 */
export function getDuplicates(arr) {
  var clone = arr.slice(0)
  var uniques = [],
    dupes = []
  while (clone.length > 0) {
    var val = clone.pop()
    if (uniques.includes(val)) {
      if (dupes.includes(val) == false) dupes.push(val)
    } else {
      uniques.push(val)
    }
  }
  return dupes
}

/**
 * Get the closest matching number from an array.
 * Modified from: https://stackoverflow.com/a/19277804/837649
 * @param {Array<number>} targets
 * @param {number} goal
 * @returns {number}
 */
export const closest = function(targets, goal) {
  return targets.reduce(function(prev, curr) {
    return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev
  })
}

/**
 * Creates a hashmap from an array.
 * @param {Array<string>}
 * @returns {object}
 */
export const createHashMapFromArray = function(arr) {
  var hashMap = {}
  arr.forEach(s => (hashMap[s] = true))
  return hashMap
}

/**
 * Same as createHashMapFromArray but the keys are all lower case
 * @param {Array<string>}
 * @returns {object}
 */
export const createLowerCaseHashMapFromArray = function(arr) {
  return createHashMapFromArray(arr.map(a => a.toLowerCase()))
}

/**
 * Pure function that accepts an array of integers and returns an map of groups.
 * The first value being the biggest group value, and the second value being the quantity of the group's items.
 *
 * @param {array} numbers Array of numbers that we want to split into groups
 * @param {number} args.step Step of the group number (if step is 100 then group size could be 100, 200, 300)
 * @param {number} maxGroups Max number of groups in which items could be split
 *
 * @returns {object} Object where key is the biggest number of the group and value is array of numbers in the group
 */
export const splitNumbersIntoGroups = ({
  numbers,
  step,
  maxGroups,
  minimumNonFloorNumber = 0,
}) => {
  // Map items into initial groups
  let groupsMap = [...numbers].sort().reduceRight((acc, number) => {
    const flooredNumber =
      minimumNonFloorNumber <= number
        ? Math.floor(number / step) * step
        : number
    if (!acc[flooredNumber]) {
      acc[flooredNumber] = []
    }
    acc[flooredNumber] = [...acc[flooredNumber], number]
    return acc
  }, {})

  // Repeat until all items are grouped into [maxGroups] groups
  while (Object.keys(groupsMap).length > maxGroups) {
    let closestGroups = { largerGroup: null, smallerGroup: null, diff: null }

    for (const currentGroup in groupsMap) {
      for (const checkGroup in groupsMap) {
        // Do nothing if checkGroup is the same as currentGroup
        if (currentGroup === checkGroup) continue
        // Calculate difference
        const diff = currentGroup - checkGroup

        // Compare of absolute values
        if (
          Math.abs(diff) < Math.abs(closestGroups.diff) ||
          !closestGroups.diff
        ) {
          // currentGroup is larger if diff > 0 so save it as largerGroup
          if (diff > 0) {
            closestGroups = {
              largerGroup: currentGroup,
              smallerGroup: checkGroup,
              diff: diff,
            }
          } else {
            closestGroups = {
              largerGroup: checkGroup,
              smallerGroup: currentGroup,
              diff: diff,
            }
          }
        }
      }
    }

    // Merge smaller group into the bigger one
    groupsMap[closestGroups.largerGroup] = [
      ...groupsMap[closestGroups.largerGroup],
      ...groupsMap[closestGroups.smallerGroup],
    ]

    // Remove smaller group
    delete groupsMap[closestGroups.smallerGroup]
  }

  return groupsMap
}



// WEBPACK FOOTER //
// ./src/Utils/Array/index.js